// @ts-check
import { format } from 'date-fns';
import { Calendar, CheckSquare, MessageSquare, Paperclip, User } from 'lucide-react';
import { RowCheckbox } from '../ui/RowCheckbox';
import TaskPriorityBadge from './TaskPriorityBadge';
import TaskStatusBadge from './TaskStatusBadge';

const TaskCard = ({ task, onClick = null, onStatusChange = null, draggable = false, selection = null }) => {
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'done';

  return (
    <div
      onClick={() => onClick && onClick(task)}
      className={`bg-card border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${
        isOverdue ? 'border-red-300 dark:border-red-800' : 'border-border'
      }`}
      draggable={draggable}
    >
      {/* Header: Title & Priority */}
      <div className="flex items-start justify-between gap-2 mb-2">
        {selection && (
          // Own click target so ticking the box doesn't open the task
          <span onClick={(e) => e.stopPropagation()} className="pt-1">
            <RowCheckbox selection={selection} id={task.id} label={`Select ${task.title}`} />
          </span>
        )}
        <h4 className="font-medium text-foreground flex-1 line-clamp-2">{task.title}</h4>
        <TaskPriorityBadge priority={task.priority} size="sm" />
      </div>

      {/* Description */}
      {task.description && <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{task.description}</p>}

      {/* Tags */}
      {task.tags && task.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {task.tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="inline-block px-2 py-0.5 bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 text-xs rounded-full"
            >
              #{tag}
            </span>
          ))}
          {task.tags.length > 3 && (
            <span className="inline-block px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">
              +{task.tags.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Footer: Metadata */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          {/* Due Date */}
          {task.due_date && (
            <div className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>
              <Calendar size={12} />
              {format(new Date(task.due_date), 'MMM d')}
            </div>
          )}

          {/* Comments Count */}
          {task.comment_count > 0 && (
            <div className="flex items-center gap-1">
              <MessageSquare size={12} />
              {task.comment_count}
            </div>
          )}

          {/* Attachments Count */}
          {task.attachment_count > 0 && (
            <div className="flex items-center gap-1">
              <Paperclip size={12} />
              {task.attachment_count}
            </div>
          )}

          {/* Subtasks Count */}
          {task.subtask_count > 0 && (
            <div className="flex items-center gap-1">
              <CheckSquare size={12} />
              {task.subtask_count}
            </div>
          )}
        </div>

        {/* Assignee Initial */}
        {task.assigned_to && (
          <div className="flex items-center gap-1">
            <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-medium">
              {task.assigned_to_name?.charAt(0).toUpperCase() || <User size={12} />}
            </div>
          </div>
        )}
      </div>

      {/* Status Badge at Bottom */}
      <div className="mt-3 pt-3 border-t border-border/60">
        <TaskStatusBadge status={task.status} size="sm" />
      </div>
    </div>
  );
};

export default TaskCard;
